import { FileText, RefreshCw } from 'lucide-react-native';
import { useCallback } from 'react';
import { ActivityIndicator, Pressable, ScrollView, StyleSheet } from 'react-native';
import { Box, HStack, Text, VStack } from '../../../components/primitives';
import { Button } from '../../../components/primitives/Button';
import { Sheet } from '../../../components/primitives/Sheet';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import type { WhatsAppTemplate } from '../types';

interface TemplatePickerSheetProps {
  visible: boolean;
  onClose: () => void;
  templates: WhatsAppTemplate[] | undefined;
  isLoading: boolean;
  isSyncing: boolean;
  onSync: () => void;
  onSelect: (template: WhatsAppTemplate) => void;
}

export function TemplatePickerSheet({
  visible,
  onClose,
  templates,
  isLoading,
  isSyncing,
  onSync,
  onSelect,
}: TemplatePickerSheetProps) {
  const approved = templates?.filter((t) => t.status === 'APPROVED') ?? [];

  const handleSelect = useCallback(
    (template: WhatsAppTemplate) => {
      onSelect(template);
      onClose();
    },
    [onSelect, onClose],
  );

  return (
    <Sheet visible={visible} onClose={onClose} title="Message Templates" position="bottom">
      <VStack spacing="md" style={styles.header}>
        <Button variant="outline" size="sm" onPress={onSync} disabled={isSyncing}>
          <HStack spacing="xs" align="center">
            <RefreshCw size={14} color={colors.primary.main} />
            <Text variant="caption" color={colors.primary.main}>
              {isSyncing ? 'Syncing...' : 'Sync from Meta'}
            </Text>
          </HStack>
        </Button>
      </VStack>

      <ScrollView style={styles.list}>
        {isLoading ? (
          <Box style={styles.center}>
            <ActivityIndicator color={colors.primary.main} />
          </Box>
        ) : approved.length === 0 ? (
          <VStack spacing="md" style={styles.empty}>
            <FileText size={36} color={colors.text.tertiary} />
            <Text variant="body" color={colors.text.secondary} style={styles.emptyText}>
              No approved templates found. Sync from Meta to load your templates.
            </Text>
          </VStack>
        ) : (
          <VStack spacing="sm" style={styles.templateList}>
            {approved.map((tmpl) => (
              <Pressable
                key={tmpl.id}
                onPress={() => handleSelect(tmpl)}
                style={styles.templateRow}
              >
                <HStack spacing="md" align="center">
                  <Box
                    width={40}
                    height={40}
                    borderRadius="full"
                    backgroundColor={colors.primary.muted}
                    alignItems="center"
                    justifyContent="center"
                  >
                    <FileText size={18} color={colors.primary.main} />
                  </Box>
                  <VStack spacing="xxs" style={styles.templateContent}>
                    <Text variant="body" color={colors.text.primary} style={styles.templateName}>
                      {tmpl.name}
                    </Text>
                    <HStack spacing="sm">
                      <Text variant="caption" color={colors.text.tertiary}>
                        {tmpl.language}
                      </Text>
                      <Text variant="caption" color={colors.text.tertiary}>
                        {tmpl.category}
                      </Text>
                    </HStack>
                  </VStack>
                </HStack>
              </Pressable>
            ))}
          </VStack>
        )}
      </ScrollView>
    </Sheet>
  );
}

const styles = StyleSheet.create({
  header: {
    paddingHorizontal: tokens.spacing.md,
    paddingTop: tokens.spacing.sm,
  },
  list: {
    flex: 1,
  },
  center: {
    padding: tokens.spacing.xl,
    alignItems: 'center',
  },
  empty: {
    alignItems: 'center',
    paddingVertical: tokens.spacing.xl,
    paddingHorizontal: tokens.spacing.lg,
  },
  emptyText: {
    textAlign: 'center',
  },
  templateList: {
    padding: tokens.spacing.md,
  },
  templateRow: {
    backgroundColor: colors.background.elevated,
    borderRadius: tokens.radius.md,
    borderWidth: 1,
    borderColor: colors.border.muted,
    padding: tokens.spacing.md,
  },
  templateContent: {
    flex: 1,
  },
  templateName: {
    fontWeight: tokens.fontWeight.semibold,
  },
});
